// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Banner from '../../../../elements/Banner/Banner';
import { FormattedMessage } from 'react-intl';
import AlertContainer from '../../../alert/container/AlertContainer';
import ShareListModal from '../../../wish-list/components/ShareListModal/ShareListModal';
import { resetState } from '../../duck';
import { animateScroll as scroll, scroller } from 'react-scroll';
import classNames from 'classnames/bind';
import update from 'immutability-helper';
import Loading from '../../../../elements/Loading/Loading';
import MyWishListBox, { MyWishListBoxEmpty } from '../MyWishListBox/MyWishListBox';
import WishListModal from '../WishListModal/WishListModal';
import { FormValidator } from '../../../../utils/form';
import Modal from '../../../../elements/Modal/Modal';
import style from './MyWishLists.scss';

import icons from '../../../../assets/styles/icons.scss';
import buttonStyle from '../../../../assets/styles/button.scss';

import type { WishList as WishListType } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { FormValidationResult } from '../../../../utils/form';
import type { LocaleState } from '../../../locale/duck';

type Props = {
  +fetchWishLists: () => void;
  +isFetchingWishLists?: boolean;
  +wishLists?: WishListType[];
  +page: number;
  +canAsk?: boolean;
  +fetchWishListsError?: HTTPError;
  +resetState: () => void;
  +postWishList: (req: any) => void;
  +postWishListSuccess: boolean;
  +postWishListError: HTTPError;
  +isPostingWishList: boolean;
  +putWishList: (req: any, userId: string, slug: string) => void;
  +putWishListSuccess: boolean;
  +putWishListError: HTTPError;
  +isPuttingWishList: boolean;
  +deleteWishList: (userId: string, slug: string) => void;
  +isDeletingWishList: boolean;
  +deleteWishListError: HTTPError;
  +deleteWishListSuccess: boolean;
  +localeState: LocaleState;
};

type State = {
  name: {
    value: string;
    errors?: string[];
  };
  image: {
    value: string;
    valueURI?: string;
    pristine: boolean;
    errors?: string[];
  };
  wishListTypeOption: {
    value: number;
    errors?: string[];
  };
  wishListModalOpen: boolean;
  deleteWishListModal: boolean;
  modalMode: 'create' | 'edit';
  wishListSelected?: number;
  shareListModalOpen: boolean;
};

const DEFAULT_EMPTY_WISH_LIST = {
  name: {
    value: '',
  },
  image: {
    value: '',
    pristine: true,
  },
  wishListTypeOption: {
    value: 0,
  },
};

const SCROLL_OPTIONS = {
  duration: 350,
  delay: 0,
  smooth: 'easeInOutQuart',
  offset: -100,
};

const optionTypes = ['PRIVATE', 'UNLISTED', 'PUBLIC'];

export default class MyWishLists extends Component<Props, State> {
  formValidator: FormValidator;

  constructor(props: Props) {
    super(props);
    this.state = {
      ...DEFAULT_EMPTY_WISH_LIST,
      wishListModalOpen: false,
      deleteWishListModal: false,
      modalMode: 'create',
      shareListModalOpen: false,
    };

    this.formValidator = new FormValidator({
      name: {
        required: 'required',
      },
      image: {
        required: 'required',
      },
    })
  }

  componentDidUpdate(prevProps: Props) {
    const { isFetchingWishLists, isPostingWishList, isPuttingWishList, isDeletingWishList } = this.props;

    if (prevProps.isFetchingWishLists && !isFetchingWishLists) {
      scroll.scrollTo(0, {
        duration: 400,
      });
    }

    if ((!prevProps.isPostingWishList && isPostingWishList) || (!prevProps.isPuttingWishList && isPuttingWishList) ||
      (!prevProps.isDeletingWishList && isDeletingWishList)) {
      scroll.scrollTo(0, {
        duration: 400,
      });
    }
  }

  componentDidMount() {
    const { fetchWishLists, wishLists } = this.props;
    if (!wishLists) {
      fetchWishLists();
    }
  }

  componentWillUnmount() {
    const { resetState } = this.props;
    resetState();
  }

  handleChangeInputText = (e: Event) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      const { value, name } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleFocusInputText = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleChangeImage = (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState({
          image: {
            valueURI: fileAsDataURL.toString(),
            value: '',
            pristine: false
          },
        });

      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => {
        console.log('error');
        this.setState({
          image: {
            value: '',
            errors: ['maxSize'],
            pristine: false,
          },
        });
        console.log('file reading has failed');
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  handleDeleteImage = () => {
    // We do it outside on purpose because we want to register the section at the time executed.
    this.setState({
      image: {
        valueURI: '',
        value: '',
        pristine: false,
      },
    });
  };

  handleChangeTypeOption = (value: number) => {
    this.setState({
      wishListTypeOption: {
        value,
      },
    });
  };

  validateForm = (): boolean => {
    const { name, image } = this.state;
    let resp = true;
    let fieldToScroll = '';

    const errors = this.formValidator.validateForm({
      name: name.value,
      image: image.value || image.valueURI,
    });

    if (errors.length) {
      resp = false;
      errors.forEach((error: FormValidationResult) => {
          this.setState((state: State) => update(state, {
            [error.field]: {
              errors: { $set: error.errors },
            },
          }));
        },
      );
      fieldToScroll = errors[0].field;
    }
    if (fieldToScroll) {
      scroller.scrollTo(fieldToScroll, SCROLL_OPTIONS)
    }

    return resp;
  };

  handleClickPostWishList = () => {
    const { image, wishListTypeOption, name } = this.state;
    const { postWishList } = this.props;
    const req: { [string]: string } = {
      name: name.value,
      type: optionTypes[wishListTypeOption.value],
    };

    if (image.value) {
      req.image = image.value;
    }
    if (image.valueURI) {
      req.imageURI = image.valueURI;
    }
    postWishList(req);
  };

  handleClickPutWishList = () => {

    const { image, wishListTypeOption, name, wishListSelected } = this.state;
    const { putWishList, wishLists } = this.props;

    const req: { [string]: string } = {
      name: name.value,
      type: optionTypes[wishListTypeOption.value],
    };

    if (!image.pristine) {
      if (image.value) {
        req.image = image.value;
      }
      if (image.valueURI) {
        req.imageURI = image.valueURI;
      }
    }

    if (!!wishLists && typeof wishListSelected === 'number')
      putWishList(req, wishLists[wishListSelected].user.id, wishLists[wishListSelected].slug);

  };

  handleClickDeleteWishList = () => {
    const { wishListSelected } = this.state;
    const { deleteWishList, wishLists } = this.props;
    if (!!wishLists && typeof wishListSelected === 'number')
      deleteWishList(wishLists[wishListSelected].user.id, wishLists[wishListSelected].slug);
    this.handleDeleteModalClose();
  };

  handleCreateOrEditModalOpen = (mode: string, index?: number) => {
    const { wishLists } = this.props;

    if (mode === 'create') {
      this.setState({
        ...DEFAULT_EMPTY_WISH_LIST,
        modalMode: mode,
        wishListModalOpen: true,
      });
    } else if (mode === 'edit' && typeof index === 'number' && !!wishLists) {
      let typeIndex = optionTypes.indexOf(wishLists[index].subtype);
      typeIndex = typeIndex > -1 ? typeIndex : 0;
      this.setState({
        modalMode: mode,
        wishListModalOpen: true,
        wishListSelected: index,
        name: {
          value: wishLists[index].name,
        },
        image: {
          value: wishLists[index].image,
          pristine: true,
        },
        wishListTypeOption: {
          value: typeIndex,
        }
      });
    }

  };

  handleShareOpen = (index: number) => {
    this.setState({
      shareListModalOpen: true,
      wishListSelected: index,
    });
  };

  handleShareListModalClose = () => this.setState({ shareListModalOpen: false, wishListSelected: undefined });

  handleWishListModalClose = () => this.setState({ wishListModalOpen: false, wishListSelected: undefined });

  handleDeleteModalOpen = (index: number) => {
    this.setState({
      deleteWishListModal: true,
      wishListSelected: index,
    });
  };

  handleDeleteModalClose = () => this.setState({ deleteWishListModal: false, wishListSelected: undefined });

  render() {
    const {
      wishLists, isFetchingWishLists, isPostingWishList, isPuttingWishList, isDeletingWishList,
      localeState,
    } = this.props;
    const {
      name, image, wishListTypeOption, modalMode,
      wishListModalOpen, deleteWishListModal, shareListModalOpen, wishListSelected,
    } = this.state;
    const isLoading = isFetchingWishLists || isPostingWishList || isPuttingWishList || isDeletingWishList;
    return (
      <Fragment>
        <Banner color="blue"
                titleElement={<FormattedMessage id="myWishLists.banner.title">{
                  txt => <h1><i className={icons.symbolHeart}/> {txt}</h1>
                }</FormattedMessage>}/>

        <section className={style.myWishListsTextWrap}>
          <section className={style.alertWrapper}>
            <AlertContainer showId="myWishLists"/>
          </section>
          {!!wishLists && !isLoading && <Fragment>
            <FormattedMessage id="myWishLists.create.title">{
              text => <p className={style.createWishList}
                         onClick={() => this.handleCreateOrEditModalOpen('create')}><i
                className={icons.iconPlusLight}/>{text}</p>
            }</FormattedMessage>
            <FormattedMessage id="myWishLists.created.title">{
              txt => <p className={style.createdLists}>{txt} {wishLists.length}</p>
            }</FormattedMessage>
          </Fragment>}
        </section>

        <section className={style.myWishListsWrap}>
          {isLoading ?
            <Loading className={style.loading}
                     color="lightBlue"/> :
            <section className={style.myWishListsContent}>
              {!!wishLists && wishLists.map((wishList: WishListType, index: number) =>
                <MyWishListBox key={index}
                               wishList={wishList}
                               handleShareOpen={this.handleShareOpen}
                               handleCreateOrEditModalOpen={this.handleCreateOrEditModalOpen}
                               handleDeleteModalOpen={this.handleDeleteModalOpen}
                               index={index}/>)}
              {!!wishLists && wishLists.length % 3 === 2 && <MyWishListBoxEmpty/>}
            </section>}
        </section>
        <WishListModal mode={modalMode}
                       handleCloseModal={this.handleWishListModalClose}
                       openModal={wishListModalOpen}
                       createWishList={this.handleClickPostWishList}
                       editWishList={this.handleClickPutWishList}
                       handleChangeImage={this.handleChangeImage}
                       handleDeleteImage={this.handleDeleteImage}
                       handleChangeInputText={this.handleChangeInputText}
                       handleFocusInputText={this.handleFocusInputText}
                       handleChangeTypeOption={this.handleChangeTypeOption}
                       validateForm={this.validateForm}
                       name={name}
                       image={image}
                       wishListTypeOption={wishListTypeOption}/>
        <Modal open={deleteWishListModal}
               handleClose={this.handleDeleteModalClose}
               width={544}
               height={504}>
          <section className={style.deleteModal}>
            <FormattedMessage id="myWishLists.deleteModal.title">{
              text => <h2>{text}</h2>
            }</FormattedMessage>
            <FormattedMessage id="myWishLists.deleteModal.button.delete">{
              text => <button className={classNames(style.delete, buttonStyle.btn, buttonStyle.red)}
                              onClick={this.handleClickDeleteWishList}>{text}</button>
            }</FormattedMessage>
            <FormattedMessage id="myWishLists.deleteModal.button.cancel">{
              text => <button className={classNames(style.cancelModal, buttonStyle.btn, buttonStyle.darkGrey)}
                              onClick={this.handleDeleteModalClose}>{text}</button>
            }</FormattedMessage>
          </section>
        </Modal>
        {!!wishLists && typeof wishListSelected === 'number' &&
        <ShareListModal openModal={shareListModalOpen}
                        handleClose={this.handleShareListModalClose}
                        localeState={localeState}
                        wishList={wishLists[wishListSelected]}/>}
      </Fragment>
    );
  }
}

MyWishLists.propTypes = {
  fetchWishLists: PropTypes.func.isRequired,
  isFetchingWishLists: PropTypes.bool,
  wishLists: PropTypes.array,
  page: PropTypes.number,
  canAsk: PropTypes.bool,
  fetchWishListsError: PropTypes.object,
  resetState: PropTypes.func.isRequired,
  postWishList: PropTypes.func.isRequired,
  postWishListError: PropTypes.object,
  isPostingWishList: PropTypes.bool,
  putWishList: PropTypes.func.isRequired,
  putWishListError: PropTypes.object,
  isPuttingWishList: PropTypes.bool,
  deleteWishList: PropTypes.func.isRequired,
  deleteWishListError: PropTypes.object,
  isDeletingWishList: PropTypes.bool,
  localeState: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/my-wish-lists/components/MyWishLists/MyWishLists.jsx